import { useEffect, useState } from "react";
import type { QuizListItem, SharedQuizListItem } from "../../../shared/types";
import { Ico } from "../../components/shell";
import { AtlShell, AtlSearch, QuizMark, StatusChip, ATL_ANS, XIcon } from "../../components/atelier";
import { navigate } from "../../router";
import { api, ApiError, hostQuiz } from "../../api";
import { alertDialog, confirmDialog } from "../../components/Dialog";
import LaunchModal from "../host/LaunchModal";
import ShareModal from "../../components/ShareModal";
import NewQuizModal from "../../components/NewQuizModal";
import AiModal from "../../components/AiModal";

export default function Quizzes() {
  const [quizzes, setQuizzes] = useState<QuizListItem[] | null>(null);
  const [shared, setShared] = useState<SharedQuizListItem[] | null>(null);
  const [shareFor, setShareFor] = useState<QuizListItem | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [launch, setLaunch] = useState<{ id: string; title: string } | null>(null);
  const [query, setQuery] = useState("");
  const [chooserOpen, setChooserOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);

  function reload() {
    api.listQuizzes().then((r) => setQuizzes(r.quizzes)).catch(() => setQuizzes([]));
    api.listSharedQuizzes().then((r) => setShared(r.quizzes)).catch(() => setShared([]));
  }
  useEffect(reload, []);

  function host(id: string) {
    const q = [...(quizzes ?? []), ...(shared ?? [])].find((x) => x.id === id);
    setLaunch({ id, title: q?.title ?? "Live quiz" });
  }

  async function duplicate(id: string) {
    try {
      await api.duplicateQuiz(id);
      reload();
    } catch (e) {
      alertDialog(e instanceof ApiError ? e.message : "Could not duplicate the quiz");
    }
  }

  async function del(q: QuizListItem) {
    const ok = await confirmDialog({
      title: "Delete quiz?",
      message: `“${q.title}” will be moved to the recycle bin. An admin can restore it from the Team area.`,
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.deleteQuiz(q.id);
      reload();
    } catch (e) {
      alertDialog(e instanceof ApiError ? e.message : "Could not delete the quiz");
    }
  }

  async function leaveShared(q: SharedQuizListItem) {
    const ok = await confirmDialog({
      title: "Remove from your list?",
      message: `“${q.title}” will be removed from your shared list. You can be re-added by the owner.`,
      tone: "danger",
      confirmLabel: "Remove",
    });
    if (!ok) return;
    try {
      await api.leaveSharedQuiz(q.id);
      setShared((cur) => (cur ? cur.filter((x) => x.id !== q.id) : cur));
    } catch (e) {
      alertDialog(e instanceof ApiError ? e.message : "Could not remove this quiz");
    }
  }

  async function cleanupEmpty() {
    const empties = (quizzes ?? []).filter((q) => q.status === "draft" && q.questionCount === 0);
    if (empties.length === 0) {
      alertDialog({ title: "Nothing to clear", message: "There are no empty drafts to remove." });
      return;
    }
    const ok = await confirmDialog({
      title: "Clear empty drafts?",
      message: `${empties.length} empty draft${empties.length === 1 ? "" : "s"} will be moved to the recycle bin.`,
      tone: "danger",
      confirmLabel: "Clear drafts",
    });
    if (!ok) return;
    try {
      await api.cleanupEmptyDrafts();
      reload();
    } catch (e) {
      alertDialog(e instanceof ApiError ? e.message : "Could not clear empty drafts");
    }
  }

  if (quizzes === null) {
    return (
      <AtlShell active="Quizzes" title="Quizzes" sub="LOADING YOUR KITCHEN">
        <div style={{ flex: 1, display: "grid", placeItems: "center", padding: 40 }}>
          <div style={{ textAlign: "center", color: "var(--atl-soft)" }}>
            <div className="atl-serif" style={{ fontSize: 22, color: "var(--atl-ink)" }}>Preheating…</div>
            <p style={{ fontSize: 13.5, fontWeight: 500, marginTop: 6 }}>Gathering your quizzes.</p>
          </div>
        </div>
      </AtlShell>
    );
  }

  const emptyCount = quizzes.filter((q) => q.status === "draft" && q.questionCount === 0).length;

  const ql = query.trim().toLowerCase();
  const visibleQuizzes = ql ? quizzes.filter((q) => q.title.toLowerCase().includes(ql)) : quizzes;
  const visibleShared = ql ? (shared ?? []).filter((q) => q.title.toLowerCase().includes(ql)) : shared ?? [];
  const noMatches = ql !== "" && visibleQuizzes.length === 0 && visibleShared.length === 0;

  const actions = (
    <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
      <AtlSearch placeholder="Search quizzes…" w={230} value={query} onChange={setQuery} />
      {emptyCount > 0 && (
        <button className="atl-btn atl-btn--ghost" onClick={cleanupEmpty}>
          <Ico name="bank" size={15} /> Delete empty drafts ({emptyCount})
        </button>
      )}
      <button className="atl-btn atl-btn--primary" onClick={() => setChooserOpen(true)}>
        <Ico name="plus" size={16} /> Create quiz
      </button>
    </div>
  );

  return (
    <>
      <AtlShell
        active="Quizzes"
        title="Quizzes"
        sub={`${quizzes.length} QUIZ${quizzes.length === 1 ? "" : "ZES"} IN YOUR KITCHEN`}
        actions={actions}
      >
        <div style={{ flex: 1, overflow: "auto", padding: "22px 30px", display: "flex", flexDirection: "column", gap: 10 }}>
          {quizzes.length === 0 && !ql && (
            <div style={{ color: "var(--atl-soft)", fontWeight: 500, fontSize: 14 }}>
              No quizzes yet —{" "}
              <span className="atl-link2" onClick={() => setChooserOpen(true)}>bake your first one</span>.
            </div>
          )}
          {noMatches && (
            <div style={{ color: "var(--atl-soft)", fontWeight: 500, fontSize: 14 }}>
              No quizzes match “{query.trim()}”.
            </div>
          )}

          {visibleQuizzes.map((r, i) => (
            <div key={r.id} className="atl-row">
              <span className="bk-clickable" onClick={() => navigate(`/builder/${r.id}`)} style={{ cursor: "pointer", flex: "0 0 auto" }}>
                <QuizMark c={ATL_ANS[i % 4]} emoji={r.coverEmoji} />
              </span>
              <div className="bk-clickable" style={{ flex: 1, minWidth: 0, cursor: "pointer" }} onClick={() => navigate(`/builder/${r.id}`)}>
                <div className="atl-serif" style={{ fontSize: 17 }}>{r.title}</div>
                <div style={{ display: "flex", gap: 9, marginTop: 5, alignItems: "center", flexWrap: "wrap", rowGap: 5 }}>
                  <StatusChip status={r.status} />
                  <span style={{ fontSize: 12.5, color: "var(--atl-faint)", fontWeight: 500 }}>
                    {r.questionCount} question{r.questionCount === 1 ? "" : "s"} · {r.scoring} scoring
                  </span>
                </div>
              </div>
              <div className="atl-rowactions" style={{ display: "flex", alignItems: "center", gap: 7 }}>
                <button className="atl-btn atl-btn--ghost" style={{ padding: "9px 15px", fontSize: 13 }} onClick={() => navigate(`/builder/${r.id}`)}>Edit</button>
                <button className="atl-btn atl-btn--ghost" style={{ padding: "9px 15px", fontSize: 13 }} onClick={() => duplicate(r.id)}>Duplicate</button>
                <button className="atl-btn atl-btn--ghost" style={{ padding: "9px 15px", fontSize: 13 }} onClick={() => setShareFor(r)}>Share{r.shareCount ? ` (${r.shareCount})` : ""}</button>
                <button className="atl-iconbtn atl-iconbtn--danger" title="Delete quiz" onClick={() => del(r)}>
                  <XIcon size={14} />
                </button>
                <button className="atl-btn atl-btn--primary" style={{ padding: "9px 17px", fontSize: 13 }} disabled={r.questionCount === 0 || !!busyId} onClick={() => host(r.id)}>
                  <Ico name="play" size={13} /> Host
                </button>
              </div>
            </div>
          ))}

          {visibleShared.length > 0 && (
            <>
              <div className="atl-eyebrow" style={{ marginTop: 16, marginBottom: 2 }}>SHARED WITH YOU</div>
              {visibleShared.map((r, i) => (
                <div key={r.id} className="atl-row">
                  <span className="bk-clickable" onClick={() => navigate(`/quiz/${r.id}`)} style={{ cursor: "pointer", flex: "0 0 auto" }}>
                    <QuizMark c={ATL_ANS[i % 4]} emoji={r.coverEmoji} />
                  </span>
                  <div className="bk-clickable" style={{ flex: 1, minWidth: 0, cursor: "pointer" }} onClick={() => navigate(`/quiz/${r.id}`)}>
                    <div className="atl-serif" style={{ fontSize: 17 }}>{r.title}</div>
                    <div style={{ display: "flex", gap: 9, marginTop: 5, alignItems: "center", flexWrap: "wrap", rowGap: 5 }}>
                      <span className="atl-chip" style={{ fontSize: 11.5, padding: "4px 11px" }}>shared by {r.sharedBy}</span>
                      <span style={{ fontSize: 12.5, color: "var(--atl-faint)", fontWeight: 500 }}>
                        {r.questionCount} question{r.questionCount === 1 ? "" : "s"} · {r.scoring} scoring
                      </span>
                    </div>
                  </div>
                  <div className="atl-rowactions" style={{ display: "flex", alignItems: "center", gap: 7 }}>
                    <button className="atl-btn atl-btn--ghost" style={{ padding: "9px 15px", fontSize: 13 }} onClick={() => navigate(`/quiz/${r.id}`)}>View</button>
                    <button className="atl-btn atl-btn--ghost" style={{ padding: "9px 15px", fontSize: 13 }} onClick={() => duplicate(r.id)}>Duplicate</button>
                    <button className="atl-iconbtn atl-iconbtn--danger" title="Remove from my list" onClick={() => leaveShared(r)}>
                      <XIcon size={14} />
                    </button>
                    <button className="atl-btn atl-btn--primary" style={{ padding: "9px 17px", fontSize: 13 }} disabled={r.questionCount === 0 || !!busyId} onClick={() => host(r.id)}>
                      <Ico name="play" size={13} /> Host
                    </button>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </AtlShell>
      {shareFor && <ShareModal quizId={shareFor.id} quizTitle={shareFor.title} onClose={() => { setShareFor(null); reload(); }} />}
      {chooserOpen && (
        <NewQuizModal
          onClose={() => setChooserOpen(false)}
          onPickAI={() => {
            setChooserOpen(false);
            setAiOpen(true);
          }}
          onPickManual={() => {
            setChooserOpen(false);
            navigate("/builder");
          }}
        />
      )}
      <AiModal open={aiOpen} onClose={() => setAiOpen(false)} />
      {launch && (
        <LaunchModal quizId={launch.id} quizTitle={launch.title} onClose={() => setLaunch(null)} />
      )}
    </>
  );
}
